<template>
  <div class="page-container">
    <template v-if="!loadingSubscriptionUsers && !loadingActiveSubscription">
      <div class="page-container-body">
        <h3 class="page-title">Manage Users</h3>

        <div class="plan-badge d-inline-flex mb-8">
          <template v-if="hasEndedTrial">Your {{ brand.name }} free trial has ended.</template>
          <template v-else>You are admin of your {{ brand.name }} {{ planLabel }}.</template>
        </div>

        <div class="users-info">
          <div class="users-container">
            <div class="users-count">
              <v-icon size="large" color="primary">mdi-account-multiple-outline</v-icon>
              <span>{{ usersCountDisplay }}</span>
              <v-tooltip v-if="quantityExceeded" location="bottom" max-width="350">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props" color="orange-darken-2">mdi-information-outline</v-icon>
                </template>
                <span>
                  You have exceeded the number of seats in your enterprise plan. Your new users will still have full access. Your {{ brand.name }} Account Manager will contact you to adjust your plan.
                </span>
              </v-tooltip>
            </div>

            <div class="users-list">
              <div
                v-for="subscriptionUser in sortedSubscriptionUsers"
                class="users-list-item"
                :class="{isMe: subscriptionUser.email === currentUser?.email}"
                :key="subscriptionUser.id">
                <div>
                  {{ subscriptionUser.email }} <strong
                    v-if=" subscriptionUser.email === currentUser?.email">(You)</strong>
                </div>
                <div class="users-list-item-actions" v-if=" subscriptionUser.email !== currentUser?.email">
                  <v-menu
                    location="bottom right"
                    :offset="[12, 0]"
                  >
                    <template v-slot:activator="{ props }">
                      <span
                        v-bind="props"
                        class="actions-button"
                      >
                        <v-icon color="#878F99">mdi-dots-horizontal</v-icon>
                      </span>
                    </template>

                    <v-list density="compact">
                      <v-list-item @click="removeUser(subscriptionUser.email)" prepend-icon="mdi-close">
                        <v-list-item-title>Remove</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </div>

            <div class="add-user-form" v-if="subscriptionAccessType === 'FULL'">
              <div class="mb-4">
                <add-subscription-user-field
                  :modelValue="emailInputValue"
                  :emails="emails"
                  @update:modelValue="(value) => emailInputValue = value"
                  @updateEmails="updateEmails"
                  :disabled="loadingAddUsersToSubscription || loadingValidateUsersSubscriptions"
                />
              </div>
              <div class="d-flex justify-end">
                <v-btn
                  size="large"
                  variant="flat"
                  :loading="loadingAddUsersToSubscription || loadingValidateUsersSubscriptions"
                  :disabled="!isEmailsValid() || loadingAddUsersToSubscription || loadingValidateUsersSubscriptions"
                  @click="addNewUser"
                >
                  Add {{ emails.length ? emails.length : '' }} new member
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="page-container-footer">
        <div class="plan-badge w-100">
          <v-icon>mdi-information-outline</v-icon>
          <span v-if="subscriptionAccessType === 'FULL'">
            All users added will receive an email that they've been invited to your {{ brand.name }} {{ planLabel }}.
          </span>
          <span v-else>
            You are now in limited access mode. Please upgrade or renew your plan from the Plans page to modify users in your plan.
          </span>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="page-loading-container-body">
        <v-progress-circular
          indeterminate
          color="white"
          size="32"
          width="2">
        </v-progress-circular>
        <span>Loading subscription...</span>
      </div>
    </template>

    <info-message-dialog
      v-if="infoMessageDialogData"
      :is-open="infoMessageDialogData.isOpen"
      :title="infoMessageDialogData.title"
      :message="infoMessageDialogData.message"
      @close="infoMessageDialogData = null"
    />

    <add-users-to-subscription-dialog
      v-if="addUsersToSubscriptionDialogData"
      :is-open="addUsersToSubscriptionDialogData.isOpen"
      :users="addUsersToSubscriptionDialogData.users"
      @close="addUsersToSubscriptionDialogData = null"
      @onAdd="onAdd"
    />
  </div>
</template>

<script>
import store from '@/store'
import { mapState } from 'vuex'
import { validateEmail } from '@/utils/validate'
import { ENTERPRISE_PLAN, getPlanByPriceId, getScheduledQuantity, hasEndedUnchosenTrial } from '@/utils/payments'
import InfoMessageDialog from '@/components/InfoMessageDialog.vue'
import AddSubscriptionUserField from '@/components/AddSubscriptionUserField.vue'
import AddUsersToSubscriptionDialog from '@/components/AddUsersToSubscriptionDialog.vue'
import brand from '@/composables/useBrand'

export default {
  components: { AddSubscriptionUserField, InfoMessageDialog, AddUsersToSubscriptionDialog },
  computed: {
    brand: () => brand,
    ...mapState({
      currentUser: state => state.user.user,
      subscriptionUsers: state => state.payments.subscriptionUsers,
      activeSubscription: state => state.payments.activeSubscription,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      loadingSubscriptionUsers: state => state.payments.loadingSubscriptionUsers,
      loadingActiveSubscription: state => state.payments.loadingActiveSubscription,
      loadingAddUsersToSubscription: state => state.payments.loadingAddUsersToSubscription,
      loadingValidateUsersSubscriptions: state => state.payments.loadingValidateUsersSubscriptions,
      loadingRemoveUserFromSubscription: state => state.payments.loadingRemoveUserFromSubscription,
      isEnterpriseUser: state => state.payments.isEnterpriseUser,
      isSubscriptionAdmin: state => state.payments.isSubscriptionAdmin
    }),
    selectedPlan: function () {
      if (this.activeSubscription) {
        if (this.isEnterpriseUser) {
          return ENTERPRISE_PLAN
        }
        return getPlanByPriceId(this.activeSubscription?.subscriptionDetails?.price?.id ||
          this.activeSubscription?.lastSubscriptionDetails?.price?.id)
      }
      return null
    },
    hasEndedTrial: function () {
      return hasEndedUnchosenTrial(this.activeSubscription)
    },
    planLabel: function () {
      return this.activeSubscription?.subscriptionDetails?.status === 'trialing' ? 'free trial' : `${this.selectedPlan?.name} plan`
    },
    usersCountDisplay: function () {
      const currentUsers = this.subscriptionUsers?.length || 0
      const currentSeats = this.activeSubscription?.subscriptionDetails?.quantity ||
        this.activeSubscription?.lastSubscriptionDetails?.quantity || 0
      const scheduledSeats = getScheduledQuantity(this.activeSubscription)

      if (scheduledSeats !== undefined && scheduledSeats !== currentSeats) {
        return `${currentUsers}/${currentSeats} Users (${currentUsers}/${scheduledSeats} next billing cycle)`
      }

      return `${currentUsers}/${currentSeats} Users`
    },
    quantityExceeded: function () {
      if (!this.isEnterpriseUser) {
        return false
      }
      const currentUsers = this.subscriptionUsers?.length || 0
      const quantity = this.activeSubscription?.subscriptionDetails?.quantity || 0
      return currentUsers > quantity
    },
    sortedSubscriptionUsers: function () {
      if (this.subscriptionUsers?.length > 0) {
        return [...this.subscriptionUsers].sort((a, b) => {
          if (a.email === this.currentUser.email) {
            return -1
          }
          if (b.email === this.currentUser.email) {
            return 1
          }

          return new Date(a.updatedAt) - new Date(b.updatedAt)
        })
      }
      return []
    }
  },
  data () {
    return {
      emails: [],
      emailInputValue: '',
      infoMessageDialogData: null,
      addUsersToSubscriptionDialogData: null
    }
  },
  methods: {
    isEmailsValid () {
      if (this.emails.length > 0) {
        return true
      }

      const emailsArray = Array.from(new Set((this.emailInputValue || '').split(',').map((e) => e.trim())))
      const validEmails = emailsArray.filter((email) => !!validateEmail(email) && !this.emails.includes(email))
      return validEmails.length !== 0
    },
    async addNewUser () {
      try {
        if (!this.emails.length) {
          return
        }

        const usersToAdd = []
        this.emails.forEach((email) => {
          const alreadyExists = this.subscriptionUsers.findIndex((user) => user.email === email) !== -1
          if (alreadyExists) {
            throw new Error(`User with ${email} email is already part of your subscription plan and cannot be added again`)
          }
          const newUser = { email }
          usersToAdd.push(newUser)
        })

        const response = await store.dispatch('payments/validateUsersSubscriptions', { users: usersToAdd })

        const usersWithSubscription = []
        const usersWithoutSubscription = []
        if (response.success && response.users.length > 0) {
          response.users.forEach((user) => {
            if (user.status === 'active') {
              usersWithSubscription.push(user)
            } else {
              const newUser = { email: user.email }
              usersWithoutSubscription.push(newUser)
            }
          })

          if (usersWithSubscription.length > 0) {
            const commaSeparatedEmails = usersWithSubscription.map((user) => user.email).join(', ')
            const isSingleUser = usersWithSubscription.length === 1

            const message = isSingleUser
              ? `The user with email ${commaSeparatedEmails} already has an active subscription. You don't need to add them to your plan. Instead, leave this page and invite them directly to the project you want them to access.`
              : `The users with emails ${commaSeparatedEmails} already have active subscriptions. You don't need to add them to your plan. Instead, leave this page and invite them directly to the projects you want them to access.`

            this.infoMessageDialogData = {
              message,
              isOpen: true
            }
            return
          }
        }

        if (usersWithoutSubscription.length > 0) {
          if (this.isEnterpriseUser) {
            await this.addEnterpriseMembers(usersWithoutSubscription)
          } else {
            this.addUsersToSubscriptionDialogData = {
              isOpen: true,
              users: usersWithoutSubscription
            }
          }
        }
      } catch (error) {
        this.infoMessageDialogData = {
          message: error.message,
          isOpen: true
        }
      }
    },
    async addEnterpriseMembers (users) {
      const response = await store.dispatch('payments/addUsersToEnterprisePlan', { users })
      if (response?.success) {
        this.emailInputValue = ''
        this.emails = []
        store.commit('snackbar/showMessage', { content: response.message }, { root: true })
      } else {
        this.infoMessageDialogData = {
          message: response?.message || 'Failed to add members.',
          isOpen: true
        }
      }
    },
    updateEmails (emails) {
      this.emails = emails
    },
    onAdd () {
      this.emailInputValue = ''
      this.emails = []
      this.addUsersToSubscriptionDialogData = null
    },
    removeUser (email) {
      store.dispatch('payments/removeUserFromSubscription', { email })
    }
  },
  watch: {
    isSubscriptionAdmin: function (val) {
      if (val === false) {
        this.$router.push('/subscription/my-plan')
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-container-body {
  flex-grow: 1;
  overflow: auto;
}

.page-loading-container-body {
  gap: 16px;
  height: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  color: var(--dark-4, #363A40);
}

.page-container-footer {
  gap: 8px;
  padding: 8px;
  display: flex;
  align-items: center;
}

.page-title {
  margin-bottom: 32px;
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.plan-badge {
  gap: 8px;
  padding: 8px;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  border-radius: 8px;
  align-items: center;
  color: var(--dark-4, #363A40);
  background: var(--light-2, #F5F2F0);
}

.users-info {
  padding: 24px;
}

.users-count {
  gap: 8px;
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
  color: var(--dark-4, #363A40);
}

.users-list {
  display: flex;
  margin-bottom: 32px;
  flex-direction: column;
}

.users-list-item {
  gap: 8px;
  height: 34px;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  align-items: center;
  align-self: stretch;
  padding: 6px 8px 8px 8px;
  justify-content: space-between;
  color: var(--dark-4, #363A40);
  border-bottom: 1px solid var(--light-3, #E5E3E1);

  &:first-child {
    border-top: 1px solid var(--light-3, #E5E3E1);
  }
}

.actions-button {
  cursor: pointer;
}
</style>
